<template>
  <div
    class="bui-tbl"
    role="table"
    :aria-rowcount="items.length + 1"
    :aria-colcount="visibleColumns.length"
    :aria-busy="loading || undefined"
  >
    <!-- Horizontal scroll lives on an INNER element so the rounded corners are
         clipped by the non-scrolling `.bui-tbl` (symmetric `overflow: hidden`).
         A scroll container can't antialias its own `border-radius` corners
         cleanly, which left a 1px white sliver at the corners. -->
    <div class="bui-tbl__scroll">
    <div class="bui-tbl-row" role="row" :aria-rowindex="1">
      <TblColumn
        v-for="(col, ci) in visibleColumns"
        :key="`col-${col.key}`"
        :width="col.width"
        :min-width="col.minWidth"
        :grow="col.grow ?? !col.width"
        :is-first="ci === 0"
        :is-last="ci === visibleColumns.length - 1"
      >
        <template #header>
          <TblColHeader
            :label="col.label"
            :kind="headerKindFor(col)"
            :sortable="!!col.sortable"
            :sort-direction="sortDirFor(col)"
            :is-first="ci === 0"
            :is-last="ci === visibleColumns.length - 1"
            :aria-colindex="ci + 1"
            :data-testid="col.headerTestId"
            @sort-click="onSortClick(col)"
          >
            <template v-if="col.selectable" #control>
              <TblCheckbox
                :model-value="allSelected"
                :indeterminate="someSelected && !allSelected"
                aria-label="Select all rows"
                data-testid="list-select-all"
                @update:model-value="onSelectAll"
              />
            </template>
          </TblColHeader>
        </template>

        <template #rows>
          <!--
            Initial-load skeleton: placeholder rows in the same column/cell grid
            so shimmer bars line up under each visible column. Only for the
            first load (no items yet) — revalidation keeps showing stale rows.
          -->
          <template v-if="showSkeleton">
            <TblRowCell
              v-for="si in SKELETON_ROW_COUNT"
              :key="`skel-${col.key}-${si}`"
              :kind="rowKindFor(col)"
              :is-first="ci === 0"
              :is-last="ci === visibleColumns.length - 1"
              :is-last-row="si === SKELETON_ROW_COUNT"
              :preview="rowHasPreview"
              aria-hidden="true"
            >
              <span v-if="col.selectable" class="skel skel--checkbox" />
              <!-- Mirror the data cell: selectable columns can also carry content. -->
              <template v-if="col.cellComponent || col.render">
                <template v-if="skelShapeFor(col) === 'thumb-text'">
                  <span class="skel skel--thumb" />
                  <span class="skel skel--bar" :style="{ width: skelBarWidth(ci, si) }" />
                </template>
                <span v-else-if="skelShapeFor(col) === 'thumb'" class="skel skel--thumb" />
                <span v-else-if="skelShapeFor(col) === 'action'" class="skel skel--action" />
                <span v-else class="skel skel--bar" :style="{ width: skelBarWidth(ci, si) }" />
              </template>
            </TblRowCell>
          </template>
          <template v-else>
          <TblRowCell
            v-for="(item, ri) in items"
            :key="`cell-${col.key}-${getId(item, ri)}`"
            :kind="rowKindFor(col)"
            :is-first="ci === 0"
            :is-last="ci === visibleColumns.length - 1"
            :is-last-row="ri === items.length - 1"
            :selected="isSelected(item)"
            :hovered="hoveredId === getId(item, ri)"
            :preview="rowHasPreview"
            :class="col.cellClass"
            :aria-rowindex="ri + 2"
            :aria-colindex="ci + 1"
            @click="onCellClick($event, item, ri)"
            @mouseenter="hoveredId = getId(item, ri)"
            @mouseleave="hoveredId = null"
          >
            <template v-if="col.selectable">
              <TblCheckbox
                :model-value="isSelected(item)"
                :aria-label="`Select row ${ri + 1}`"
                :data-testid="`list-row-select-${ri + 1}`"
                @update:model-value="(v) => onSelectRow(item, v)"
              />
            </template>
            <component
              v-if="col.cellComponent"
              :is="col.cellComponent"
              :item="item"
              :itemIndex="ri"
              :isHovered="hoveredId === getId(item, ri)"
              :isSelected="isSelected(item)"
            />
            <RenderCell
              v-else-if="col.render"
              :renderFn="col.render"
              :item="item"
              :itemIndex="ri"
              :isHovered="hoveredId === getId(item, ri)"
              :isSelected="isSelected(item)"
            />
          </TblRowCell>
          </template>
        </template>
      </TblColumn>
    </div>
    </div>

    <div v-if="error && !loading" class="error" role="alert">
      <span class="error__message">{{ typeof error === 'string' && error ? error : 'Something went wrong while loading this table.' }}</span>
      <button type="button" class="error__retry" @click="emit('retry')">Retry</button>
    </div>

    <div v-else-if="!items.length && !loading" class="empty">
      {{ emptyMessage || 'No items to display.' }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineComponent, h, ref } from 'vue';
import TblColumn from '../../molecules/TblColumn/TblColumn.vue';
import TblColHeader from '../../molecules/TblColHeader/TblColHeader.vue';
import TblRowCell from '../../molecules/TblRowCell/TblRowCell.vue';
import TblCheckbox from '../../atoms/TblCheckbox/TblCheckbox.vue';
import type { BuiTblColumn } from './types';

const RenderCell = defineComponent({
  name: 'RenderCell',
  props: {
    renderFn: { type: Function, required: true },
    item: { type: null, required: true },
    itemIndex: { type: Number, required: true },
    isHovered: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
  },
  setup(p) {
    return () => {
      const out = (p.renderFn as Function)({
        item: p.item,
        itemIndex: p.itemIndex,
        isHovered: p.isHovered,
        isSelected: p.isSelected,
      });
      // Bare strings/numbers render as text nodes, which have no box to
      // truncate — wrap them so text cells can ellipsize and keep the full
      // value reachable via the native title tooltip.
      if (typeof out === 'string' || typeof out === 'number') {
        const text = String(out);
        return h('span', { class: 'bui-cell-text', title: text || undefined }, text);
      }
      return out;
    };
  },
});

const props = withDefaults(
  defineProps<{
    columns: BuiTblColumn[];
    items: any[];
    sortKey?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    selectedIds?: Set<number | string> | (number | string)[];
    itemIdGetter?: (item: any) => number | string;
    loading?: boolean;
    emptyMessage?: string;
    /** Error state: pass a message (or `true` for a generic one) when the list fetch failed. */
    error?: string | boolean;
  }>(),
  {
    sortKey: null,
    sortDir: null,
    selectedIds: () => new Set(),
    itemIdGetter: (item: any) => item?.id,
    loading: false,
  },
);

const emit = defineEmits<{
  rowClick: [{ event: MouseEvent; item: any; index: number }];
  selectRow: [{ item: any; selected: boolean }];
  selectAll: [{ selected: boolean }];
  sort: [{ column: BuiTblColumn; nextDir: 'asc' | 'desc' }];
  retry: [];
}>();

const hoveredId = ref<number | string | null>(null);

const visibleColumns = computed(() => props.columns.filter((c) => !c.hidden));

// ---------------------------------------------------------------------------
// Initial-load skeleton
// ---------------------------------------------------------------------------
const SKELETON_ROW_COUNT = 9;

const showSkeleton = computed(() => props.loading && !props.items.length);

type SkelShape = 'bar' | 'thumb' | 'thumb-text' | 'action';

function skelShapeFor(col: BuiTblColumn): SkelShape {
  if (col.kind === 'namePreview') return 'thumb-text';
  if (col.kind === 'preview') return 'thumb';
  if (col.kind === 'actions' || col.kind === 'actionsList' || col.kind === 'usedBy') return 'action';
  return 'bar';
}

// Deterministic width variation so the skeleton reads as rows of text,
// not a uniform block. Indexed by column + row so it's stable across renders.
const SKEL_BAR_WIDTHS = ['72%', '52%', '84%', '64%', '44%'];
function skelBarWidth(ci: number, si: number) {
  return SKEL_BAR_WIDTHS[(ci * 2 + si) % SKEL_BAR_WIDTHS.length];
}

const rowHasPreview = computed(() =>
  visibleColumns.value.some(
    (c) => c.kind === 'preview' || c.kind === 'namePreview',
  ),
);

const selectedSet = computed(() => {
  if (props.selectedIds instanceof Set) return props.selectedIds;
  return new Set(props.selectedIds || []);
});

function getId(item: any, fallback: number) {
  const id = props.itemIdGetter(item);
  return id ?? fallback;
}

function isSelected(item: any) {
  return selectedSet.value.has(props.itemIdGetter(item));
}

const allSelected = computed(() =>
  props.items.length > 0 &&
  props.items.every((it) => selectedSet.value.has(props.itemIdGetter(it))),
);

const someSelected = computed(() =>
  props.items.some((it) => selectedSet.value.has(props.itemIdGetter(it))),
);

function headerKindFor(col: BuiTblColumn) {
  if (col.kind === 'namePreview') return 'namePreview';
  if (col.kind === 'preview') return 'preview';
  if (col.kind === 'actions') return 'actions';
  if (col.kind === 'actionsList') return 'actionsList';
  if (col.kind === 'usedBy') return 'usedBy';
  if (col.kind === 'name') return 'name';
  if (col.kind === 'spacerName') return 'name';
  return 'text';
}

type RowKind = 'name' | 'namePreview' | 'preview' | 'text' | 'tags' | 'status' | 'usedBy' | 'actions' | 'actionsList' | 'checkbox';

function rowKindFor(col: BuiTblColumn): RowKind {
  if (col.kind === 'spacerName') return 'name';
  return (col.kind || 'text') as RowKind;
}

function sortDirFor(col: BuiTblColumn): 'asc' | 'desc' | null {
  const k = col.sortKey || col.key;
  if (props.sortKey !== k) return null;
  return props.sortDir ?? null;
}

function onSortClick(col: BuiTblColumn) {
  if (!col.sortable) return;
  const cur = sortDirFor(col);
  const nextDir: 'asc' | 'desc' = cur === 'asc' ? 'desc' : 'asc';
  emit('sort', { column: col, nextDir });
}

function onCellClick(event: MouseEvent, item: any, index: number) {
  emit('rowClick', { event, item, index });
}

function onSelectRow(item: any, selected: boolean) {
  emit('selectRow', { item, selected });
}

function onSelectAll(selected: boolean) {
  emit('selectAll', { selected });
}

</script>

<style lang="scss" scoped>
.bui-tbl {
  position: relative;
  width: 100%;
  /* Never exceed the page — when column minimums genuinely can't fit,
     scroll INSIDE the card instead of growing it (and the page). */
  max-width: 100%;
  background: var(--bui-tbl-line, #ffd9e6);
  border-radius: var(--bui-tbl-card-radius, 12px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  /* Symmetric clip so all four rounded corners are cut cleanly. Horizontal
     scrolling is delegated to the inner `.bui-tbl__scroll`. */
  overflow: hidden;
  isolation: isolate;
}

.bui-tbl__scroll {
  /* When column minimums genuinely can't fit, scroll INSIDE the card. */
  overflow-x: auto;
  overflow-y: hidden;
}

.bui-tbl-row {
  display: flex;
  align-items: stretch;
  /* When fixed widths + column minWidths genuinely can't fit, the columns
     (which refuse to shrink below those minimums) overflow this 100%-wide
     row and .bui-tbl scrolls to reveal them. Deliberately NO
     min-width: min-content here: flex intrinsic contributions take the
     larger of content min-content and flex-basis, so nowrap cell text
     would balloon the row to the longest line instead of truncating. */
  width: 100%;
  background: var(--bui-tbl-line, #ffd9e6);
  gap: 0;
}

.empty {
  padding: 32px;
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  text-align: center;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px;
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  text-align: center;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

/*
 * Initial-load skeleton shimmer (same sweep as ContentLoadingSkeleton, but
 * driven by BUI table tokens so it follows every theme).
 */
.skel {
  display: inline-block;
  flex: 0 0 auto;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--bui-tbl-line, #ffd9e6) 25%,
    var(--bui-tbl-row-fill, #fffcfd) 50%,
    var(--bui-tbl-line, #ffd9e6) 75%
  );
  background-size: 200% 100%;
  animation: bui-tbl-skel-shimmer 1.4s ease-in-out infinite;
}

.skel--bar {
  height: 12px;
  max-width: 220px;
  flex-shrink: 1; /* yield to the checkbox in selectable name columns */
}

.skel--checkbox {
  width: var(--bui-chk-size, 20px);
  height: var(--bui-chk-size, 20px);
  border-radius: 5px;
}

.skel--thumb {
  width: var(--bui-tbl-thumb-size, 96px);
  height: var(--bui-tbl-thumb-size, 96px);
  border-radius: var(--bui-tbl-thumb-radius, 4px);
}

.skel--action {
  width: 28px;
  height: 12px;
}

@keyframes bui-tbl-skel-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skel {
    animation: none;
  }
}

.error__retry {
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  border: 0;
  border-radius: var(--bui-cta-radius, 8px);
  background: var(--bui-cta-sec-fill, #4b1886);
  color: var(--bui-cta-sec-text, #fffcfd);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    filter: brightness(1.08);
  }
}

@media (max-width: 768px) {
  .bui-tbl {
    border-radius: 9px;
    box-shadow: 0 4px 20px rgba(75, 24, 134, 0.12);
  }

  .empty {
    padding: 18px 12px;
    font-size: 13px;
  }
}
</style>
